import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type WatchlistItem } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { encodeCp949Url } from '../lib/cp949';

// cron schedule (KST 07/12/15/18). collector wrangler.toml 의 UTC 22/3/6/9 와 동기.
const CRON_HOURS_KST = [7, 12, 15, 18] as const;

/** 다음 cron 까지 남은 시간을 KST 기준으로 "X시간 Y분 후 (HH:MM)" 형식. */
function nextCronLabel(): string {
  // 현재 시각의 KST 환산 — UTC + 9h offset (browser timezone 무관).
  // Codex P3 fix: 옛 코드 가 getTimezoneOffset() 사용 → 한국 brows 시 KST = UTC 의 bug.
  const now = new Date();
  const kstMs = now.getTime() + 9 * 60 * 60 * 1000;
  const kst = new Date(kstMs);
  const minutes = kst.getUTCHours() * 60 + kst.getUTCMinutes();
  for (const h of CRON_HOURS_KST) {
    const cronMin = h * 60;
    if (cronMin > minutes) {
      const diff = cronMin - minutes;
      const hh = Math.floor(diff / 60);
      const mm = diff % 60;
      return `${hh > 0 ? `${hh}시간 ` : ''}${mm}분 후 (${String(h).padStart(2, '0')}:00 KST)`;
    }
  }
  // 모두 지나감 → 내일 첫 cron (07:00).
  const diff = 24 * 60 - minutes + CRON_HOURS_KST[0] * 60;
  const hh = Math.floor(diff / 60);
  const mm = diff % 60;
  return `${hh}시간 ${mm}분 후 (07:00 KST 내일)`;
}

export default function WatchlistPage() {
  const [reloadKey, setReloadKey] = useState(0);
  const { data, error, loading } = useAsync(() => api.watchlist(), [reloadKey]);
  // Codex P3 fix: hardcoded "batch mode, 50% 절감" 의 stale claim → settings.batchVision
  // 의 dynamic state. Phase A default 가 batchVision=false → realtime 표시.
  const { data: settings } = useAsync(() => api.settings(), []);

  if (error) return <ErrorBox msg={error} />;
  if (loading) return <p className="text-slate-500">로딩 중…</p>;
  const items = data?.items ?? [];
  const reload = () => setReloadKey((k) => k + 1);
  const batchMode = settings?.batchVision === true;

  return (
    <section>
      <h1 className="text-2xl font-semibold tracking-tight">워치리스트</h1>
      <p className="mt-1 text-sm text-slate-500">
        {items.length}개 종목 · 매일{' '}
        <span className="font-mono">07 / 12 / 15 / 18 KST</span> 자동 수집 (
        {batchMode ? 'batch mode, 50% 절감' : 'realtime mode'}) · 다음 수집{' '}
        <span className="font-medium text-slate-700">{nextCronLabel()}</span>
      </p>

      <AddForm onAdded={reload} />

      <ul className="mt-4 divide-y divide-slate-200 rounded-lg border border-slate-200 bg-white">
        {items.map((it) => (
          <Row key={it.symbol} item={it} onChanged={reload} />
        ))}
        {items.length === 0 && <li className="px-4 py-6 text-slate-500">워치리스트가 비어있습니다.</li>}
      </ul>
    </section>
  );
}

function AddForm({ onAdded }: { onAdded: () => void }) {
  const [symbol, setSymbol] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const valid = /^\d{6}$/.test(symbol) && name.trim().length > 0;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    setBusy(true);
    setErr(null);
    try {
      // 한글 → cp949 URL-encoded (네이버 itemName 호환). lazy-load 461KB lib.
      const trimmedName = name.trim();
      const encoded = await encodeCp949Url(trimmedName);
      await api.watchlistAdd(symbol, trimmedName, encoded);
      setSymbol('');
      setName('');
      onAdded();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-5 flex flex-wrap items-center gap-2">
      <input
        value={symbol}
        onChange={(e) => setSymbol(e.target.value)}
        placeholder="006400"
        maxLength={6}
        className="w-28 rounded border border-slate-300 px-2 py-1 font-mono text-sm focus:border-indigo-500 focus:outline-none"
      />
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="삼성SDI"
        maxLength={50}
        className="w-48 rounded border border-slate-300 px-2 py-1 text-sm focus:border-indigo-500 focus:outline-none"
      />
      <button
        type="submit"
        disabled={!valid || busy}
        className="rounded bg-indigo-600 px-3 py-1 text-sm text-white disabled:bg-slate-300"
      >
        {busy ? '추가 중…' : '+ 추가'}
      </button>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </form>
  );
}

function Row({ item, onChanged }: { item: WatchlistItem; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    setBusy(true);
    try {
      await api.watchlistToggle(item.symbol, !item.active);
      onChanged();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-center justify-between px-4 py-3">
      <div>
        <Link
          to={`/wikis/${item.symbol}`}
          className="font-medium text-slate-900 hover:text-indigo-600"
        >
          {item.name}
        </Link>
        <span className="ml-2 font-mono text-xs text-slate-500">{item.symbol}</span>
      </div>
      <div className="flex items-center gap-2">
        <Link
          to={`/admin/reports?symbol=${item.symbol}`}
          className="text-xs text-slate-500 hover:text-indigo-600"
          title="가용 보고서 listing — 선택 다운로드"
        >
          🔍 보고서
        </Link>
        <span className={item.active ? 'pill-buy' : 'pill bg-slate-100 text-slate-600'}>
          {item.active ? 'active' : 'paused'}
        </span>
        <button
          onClick={toggle}
          disabled={busy}
          className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-slate-50 disabled:opacity-50"
          title={item.active ? '일시정지 (cron skip)' : '재개'}
        >
          {item.active ? '⏸ 일시정지' : '▶ 재개'}
        </button>
      </div>
    </li>
  );
}

export function ErrorBox({ msg }: { msg: string }) {
  const tokenMissing = msg.includes('TOKEN_MISSING');
  return (
    <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
      <p className="font-medium">에러: {msg}</p>
      {tokenMissing && (
        <p className="mt-1">
          <Link to="/settings" className="underline">
            설정
          </Link>{' '}
          페이지에서 API 토큰을 입력하세요.
        </p>
      )}
    </div>
  );
}
